// Time periods for the CSV export, in Singapore time. Shared by the dashboard, the export
// endpoints and the tests; import-free so plain Node can load it.
//
// Dates are plain `YYYY-MM-DD` Singapore calendar dates (UTC+8, no daylight saving): a request
// made at 00:30 on the 1st belongs to the new month though it is still the 31st in UTC.

export type PeriodKind = "month" | "last" | "quarter" | "year" | "all" | "custom";

/** A closed range of Singapore dates. `null` means open on that side. */
export interface DateRange {
  from: string | null;
  to: string | null;
}

const SG_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A real calendar date written YYYY-MM-DD (2026-02-30 is not one). */
export function isIsoDate(s: unknown): s is string {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

function parts(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split("-").map(Number);
  return { y, m, d };
}

function dayStartMs(iso: string): number {
  const { y, m, d } = parts(iso);
  return Date.UTC(y, m - 1, d);
}

function isoOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Today's date in Singapore, whatever time zone the caller is in. */
export function todayInSingapore(now: number = Date.now()): string {
  return isoOf(now + SG_OFFSET_MS);
}

export function addDays(iso: string, n: number): string {
  return isoOf(dayStartMs(iso) + n * DAY_MS);
}

/** Moves a date by whole months, clamping to the last day of a shorter month:
 *  31 May minus 3 months is 28 Feb (29 in a leap year), not 3 March. */
export function addMonths(iso: string, n: number): string {
  const { y, m, d } = parts(iso);
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12;
  const last = new Date(Date.UTC(ny, nm + 1, 0)).getUTCDate();
  const day = Math.min(d, last);
  return `${String(ny).padStart(4, "0")}-${String(nm + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** What each preset period covers, given today's Singapore date. */
export function periodRange(kind: Exclude<PeriodKind, "custom">, today: string): DateRange {
  const firstOfMonth = `${today.slice(0, 8)}01`;
  switch (kind) {
    case "month":
      return { from: firstOfMonth, to: today };
    case "last":
      return { from: addMonths(firstOfMonth, -1), to: addDays(firstOfMonth, -1) };
    case "quarter":
      return { from: addMonths(today, -3), to: today };
    case "year":
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case "all":
      return { from: null, to: null };
  }
}

/** Both ends real dates (when given), and not the wrong way round. */
export function isValidRange(r: DateRange): boolean {
  if (r.from !== null && !isIsoDate(r.from)) return false;
  if (r.to !== null && !isIsoDate(r.to)) return false;
  if (r.from !== null && r.to !== null && r.from > r.to) return false;
  return true;
}

/** The range as database bounds: from 00:00 on the first day to 00:00 after the
 *  last (`created_at >= gte AND created_at < lt`), all in UTC. */
export function dayBounds(r: DateRange): { gte: string | null; lt: string | null } {
  return {
    gte: r.from === null ? null : new Date(dayStartMs(r.from) - SG_OFFSET_MS).toISOString(),
    lt: r.to === null ? null : new Date(dayStartMs(r.to) + DAY_MS - SG_OFFSET_MS).toISOString(),
  };
}

/** A stored timestamp as Singapore wall-clock time. */
export function sgt(timestamp: string): { date: string; time: string } {
  const s = new Date(new Date(timestamp).getTime() + SG_OFFSET_MS).toISOString();
  return { date: s.slice(0, 10), time: s.slice(11, 16) };
}

/** "2026-10-09 14:02": what goes in the CSV, and what Excel reads as a date. */
export function sgtDateTime(timestamp: string): string {
  const { date, time } = sgt(timestamp);
  return `${date} ${time}`;
}

/** "9 Oct 14:02", the short form used in the preview. */
export function shortSgt(timestamp: string): string {
  const { date, time } = sgt(timestamp);
  const { m, d } = parts(date);
  return `${d} ${MONTHS[m - 1]} ${time}`;
}

/** "9 Oct 2026". */
export function longDate(iso: string): string {
  const { y, m, d } = parts(iso);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** "1 – 9 Oct 2026", "9 Jul – 9 Oct 2026" or, across years, both in full. */
export function rangeLabel(r: DateRange): string {
  if (r.from === null || r.to === null) {
    if (r.from !== null) return `From ${longDate(r.from)}`;
    if (r.to !== null) return `Up to ${longDate(r.to)}`;
    return "Every request";
  }
  if (r.from === r.to) return longDate(r.from);
  const a = parts(r.from);
  const b = parts(r.to);
  if (a.y !== b.y) return `${longDate(r.from)} – ${longDate(r.to)}`;
  if (a.m === b.m) return `${a.d} – ${b.d} ${MONTHS[b.m - 1]} ${b.y}`;
  return `${a.d} ${MONTHS[a.m - 1]} – ${b.d} ${MONTHS[b.m - 1]} ${b.y}`;
}

/** `foodpantry-requests_2026-08-01_to_2026-08-31.csv`. `base` must already be
 *  file-safe (letters, digits, hyphens). */
export function exportFileName(base: string, r: DateRange): string {
  let span: string;
  if (r.from !== null && r.to !== null) span = `${r.from}_to_${r.to}`;
  else if (r.from !== null) span = `from_${r.from}`;
  else if (r.to !== null) span = `up-to_${r.to}`;
  else span = "all-time";
  return `${base}-requests_${span}.csv`;
}
